import React, { useState, useMemo, useEffect, lazy, Suspense, useRef } from 'react';
import AddMosqueForm from './components/AddMosqueForm';
import MosqueDetails from './components/MosqueDetails';
import SuggestEditForm from './components/SuggestEditForm';
import SidePanel from './components/SidePanel';
import HamburgerButton from './components/HamburgerButton';
import { FilterOptions } from './components/SearchFilter';
import ErrorMessage from './components/ErrorMessage';
import { AdminLogin } from './components/AdminLogin';
import { AdminDashboard } from './components/AdminDashboard';
import HomeLandingPage from './components/HomeLandingPage';
import { useMosques } from './hooks/useMosques';
import { usePhotos } from './hooks/usePhotos';
import { useGeolocation } from './hooks/useGeolocation';
import { useAdmin } from './hooks/useAdmin';
import { useLanguage } from './contexts/LanguageContext';
import { Mosque, MosqueFormData, PhotoUploadData } from './types/mosque';
import { trackEvent } from './lib/analytics';
import './styles/mobile.css';
import './styles/layout.css';
import './test-supabase'; // Add test import

const MapComponent = lazy(() => import('./components/MapComponent'));
const MOSQUE_QUERY_PARAM = 'mosque';
const normalizePathname = (pathname: string) => pathname.replace(/\/+$/, '') || '/';

function App() {
  const isHomeLandingPage = normalizePathname(window.location.pathname) === '/home';
  const { t } = useLanguage();
  const { mosques, addMosque, submitSuggestion, isLoading: isMosquesLoading, error: mosquesError, refetch } = useMosques();
  const { uploadPhotos } = usePhotos();
  const { isAdmin, signOut } = useAdmin();

  const { location: userLocation, isLoading: isLoadingLocation, getCurrentLocation } = useGeolocation();

  const [isAddFormOpen, setIsAddFormOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [pendingLocation, setPendingLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [selectedMosque, setSelectedMosque] = useState<Mosque | null>(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState<FilterOptions>({
    type: 'all',
    isPublic: 'all',
    facilities: {
      menWc: false,
      womenWc: false,
      menWudu: false,
      womenWudu: false,
      womenPrayerArea: false,
    },
  });
  const [isSidePanelOpen, setIsSidePanelOpen] = useState(false);
  const [shouldCenterOnLocation, setShouldCenterOnLocation] = useState(false);
  const [isAdminLoginOpen, setIsAdminLoginOpen] = useState(false);
  const [isAdminDashboardOpen, setIsAdminDashboardOpen] = useState(false);
  const [isSuggestEditOpen, setIsSuggestEditOpen] = useState(false);
  const [mosqueToEdit, setMosqueToEdit] = useState<Mosque | null>(null);
  const lastTrackedSearchRef = useRef('');
  const detailsTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [selectedMosqueFocusKey, setSelectedMosqueFocusKey] = useState(0);
  const [isInfoBannerVisible, setIsInfoBannerVisible] = useState(true);
  const [urlSyncTick, setUrlSyncTick] = useState(0);
  const lastTrackedUserLocationRef = useRef<string>('');

  const updateSelectedMosqueUrl = (mosque: Mosque | null) => {
    if (window.location.pathname !== '/') {
      return;
    }

    const url = new URL(window.location.href);
    if (mosque) {
      url.searchParams.set(MOSQUE_QUERY_PARAM, mosque.id);
      url.searchParams.set('lat', mosque.location.lat.toString());
      url.searchParams.set('lng', mosque.location.lng.toString());
      url.searchParams.set('zoom', '18');
    } else {
      url.searchParams.delete(MOSQUE_QUERY_PARAM);
    }

    window.history.replaceState({}, '', url.toString());
  };

  const openMosqueDetails = (mosque: Mosque, withDelay: boolean) => {
    setSelectedMosque(mosque);
    setSelectedMosqueFocusKey((prev) => prev + 1);

    if (detailsTimeoutRef.current) {
      clearTimeout(detailsTimeoutRef.current);
    }

    if (withDelay) {
      detailsTimeoutRef.current = setTimeout(() => {
        setIsDetailsOpen(true);
      }, 700);
    } else {
      setIsDetailsOpen(true);
    }
  };

  const handleMapClick = (lat: number, lng: number) => {
    trackEvent('map_click_add_location', {
      lat_rounded: Number(lat.toFixed(3)),
      lng_rounded: Number(lng.toFixed(3)),
    });
    // Set pending location - user will confirm before form opens
    setPendingLocation({ lat, lng });
  };

  const handleConfirmLocation = () => {
    if (pendingLocation) {
      trackEvent('add_location_confirmed');
      setSelectedLocation(pendingLocation);
      setPendingLocation(null);
      setIsAddFormOpen(true);
    }
  };

  const handleCancelPendingLocation = () => {
    trackEvent('add_location_cancelled');
    setPendingLocation(null);
  };

  const handleMosqueClick = (mosque: Mosque) => {
    trackEvent('mosque_opened', {
      mosque_id: mosque.id,
      mosque_name: mosque.name,
      mosque_type: mosque.type,
      is_public: mosque.isPublic,
    });
    openMosqueDetails(mosque, false);
    updateSelectedMosqueUrl(mosque);
  };

  const handleDismissInfoBanner = () => {
    setIsInfoBannerVisible(false);
  };

  const handleAddMosque = async (formData: MosqueFormData, photos: PhotoUploadData[], submitterName?: string) => {
    if (!selectedLocation) return;

    const newMosqueData = {
      ...formData,
      location: {
        lat: selectedLocation.lat,
        lng: selectedLocation.lng,
      },
    };

    const mosqueId = await addMosque(newMosqueData, submitterName);
    if (mosqueId) {
      trackEvent('mosque_added', {
        mosque_type: formData.type,
        is_public: formData.isPublic,
        photo_count: photos.length,
      });
    }
    if (mosqueId && photos.length > 0) {
      let photoUploadFailed: unknown = null;
      try {
        await uploadPhotos(mosqueId, photos);
      } catch (error) {
        console.error('Mosque added but photo upload failed:', error);
        photoUploadFailed = error;
      }

      if (photoUploadFailed) {
        const partialUploadError = Object.assign(
          new Error('Mosque added but photo upload failed'),
          { code: 'MOSQUE_CREATED_PHOTO_UPLOAD_FAILED', cause: photoUploadFailed }
        );
        setSelectedLocation(null);
        throw partialUploadError;
      }
    }

    setSelectedLocation(null);
  };

  const handleCloseAddForm = () => {
    setIsAddFormOpen(false);
    setSelectedLocation(null);
  };

  const handleCloseDetails = () => {
    if (detailsTimeoutRef.current) {
      clearTimeout(detailsTimeoutRef.current);
      detailsTimeoutRef.current = null;
    }
    setIsDetailsOpen(false);
    setSelectedMosque(null);
    updateSelectedMosqueUrl(null);
  };

  const handleSearch = (query: string) => {
    const trimmedQuery = query.trim();
    const queryLength = trimmedQuery.length;

    if (queryLength >= 2 && trimmedQuery !== lastTrackedSearchRef.current) {
      trackEvent('search_used', { query_length: queryLength });
      lastTrackedSearchRef.current = trimmedQuery;
    } else if (queryLength === 0 && lastTrackedSearchRef.current) {
      trackEvent('search_cleared');
      lastTrackedSearchRef.current = '';
    }

    setSearchQuery(query);
  };

  const handleFilter = (newFilters: FilterOptions) => {
    const facilitiesCount = Object.values(newFilters.facilities).filter(Boolean).length;
    const activeFiltersCount =
      (newFilters.type !== 'all' ? 1 : 0) +
      (newFilters.isPublic !== 'all' ? 1 : 0) +
      facilitiesCount;

    trackEvent('filters_updated', {
      active_filters: activeFiltersCount,
      facilities_count: facilitiesCount,
      type_filter: newFilters.type,
      access_filter: newFilters.isPublic,
    });
    setFilters(newFilters);
  };

  const handleNearMe = () => {
    trackEvent('locate_me_clicked');
    setShouldCenterOnLocation(true);
    getCurrentLocation();
  };

  const openSuggestionForm = (mosque: Mosque) => {
    setMosqueToEdit(mosque);
    setIsSuggestEditOpen(true);
  };

  const handleSuggestEdit = (mosque: Mosque) => {
    trackEvent('suggest_edit_opened', { mosque_type: mosque.type });
    openSuggestionForm(mosque);
  };

  const handleCloseSuggestEdit = () => {
    setIsSuggestEditOpen(false);
    setMosqueToEdit(null);
  };

  const handleSubmitSuggestion = async (
    mosqueId: string,
    changes: Partial<MosqueFormData>,
    reason: string,
    submitterName?: string
  ) => {
    await submitSuggestion(mosqueId, changes, reason, submitterName);
    trackEvent('suggest_edit_submitted', {
      mosque_id_present: Boolean(mosqueId),
      changed_fields_count: Object.keys(changes).length,
      reason_length: reason.trim().length,
    });
  };

  // Handle URL paths
  useEffect(() => {
    const handlePath = () => {
      const path = normalizePathname(window.location.pathname);
      if (path === '/signout' || path === '/logout') {
        signOut();
      } else if (path === '/admin') {
        if (isAdmin) {
          setIsAdminDashboardOpen(true);
        } else {
          setIsAdminLoginOpen(true);
        }
      } else if (path === '/home') {
        setIsAdminDashboardOpen(false);
        setIsAdminLoginOpen(false);
      } else if (path === '/') {
        setIsAdminDashboardOpen(false);
        setIsAdminLoginOpen(false);
      }
    };

    handlePath();

    // Handle browser back/forward buttons
    const handlePopState = () => handlePath();
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [signOut, isAdmin]);

  useEffect(() => {
    const handlePopState = () => {
      setUrlSyncTick((prev) => prev + 1);
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Open or close mosque details based on URL parameter.
  useEffect(() => {
    if (window.location.pathname !== '/') {
      return;
    }

    const mosqueIdFromUrl = new URLSearchParams(window.location.search).get(MOSQUE_QUERY_PARAM);
    if (!mosqueIdFromUrl) {
      if (selectedMosque || isDetailsOpen) {
        if (detailsTimeoutRef.current) {
          clearTimeout(detailsTimeoutRef.current);
          detailsTimeoutRef.current = null;
        }
        setIsDetailsOpen(false);
        setSelectedMosque(null);
      }
      return;
    }

    const mosqueFromUrl = mosques.find((mosque) => mosque.id === mosqueIdFromUrl);
    if (!mosqueFromUrl) {
      return;
    }

    if (selectedMosque?.id === mosqueFromUrl.id && isDetailsOpen) {
      return;
    }

    openMosqueDetails(mosqueFromUrl, false);
  }, [mosques, selectedMosque, isDetailsOpen, urlSyncTick]);

  // Reset center trigger after location is used
  useEffect(() => {
    if (shouldCenterOnLocation && userLocation) {
      // Reset the flag after a short delay to allow the map to center
      const timer = setTimeout(() => {
        setShouldCenterOnLocation(false);
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [shouldCenterOnLocation, userLocation]);

  useEffect(() => {
    if (!userLocation) {
      return;
    }

    const latRounded = Number(userLocation.lat.toFixed(3));
    const lngRounded = Number(userLocation.lng.toFixed(3));
    const locationKey = `${latRounded},${lngRounded}`;
    if (lastTrackedUserLocationRef.current === locationKey) {
      return;
    }

    lastTrackedUserLocationRef.current = locationKey;
    trackEvent('user_location_set', {
      lat_rounded: latRounded,
      lng_rounded: lngRounded,
    });
  }, [userLocation]);

  // Ask location permission on startup, but do not block UI if denied.
  useEffect(() => {
    const timer = setTimeout(() => {
      getCurrentLocation();
    }, 500);

    return () => clearTimeout(timer);
  }, [getCurrentLocation]);

  // Filter and search mosques
  const filteredMosques = useMemo(() => {
    let filtered = mosques;

    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();

      filtered = filtered.filter(mosque => {
        const nameMatch = mosque.name.toLowerCase().includes(query);
        const addressMatch = mosque.location.address?.toLowerCase().includes(query);

        return nameMatch || addressMatch;
      });
    }

    // Type filter
    if (filters.type !== 'all') {
      filtered = filtered.filter(mosque => mosque.type === filters.type);
    }

    // Public/Restricted filter
    if (filters.isPublic === 'public') {
      filtered = filtered.filter(mosque => mosque.isPublic);
    } else if (filters.isPublic === 'private') {
      filtered = filtered.filter(mosque => !mosque.isPublic);
    }

    // Facilities filter
    const facilitiesEnabled = Object.entries(filters.facilities).filter(([_, enabled]) => enabled);
    if (facilitiesEnabled.length > 0) {
      filtered = filtered.filter(mosque =>
        facilitiesEnabled.every(([facility, _]) =>
          mosque.facilities[facility as keyof typeof mosque.facilities]
        )
      );
    }

    return filtered;
  }, [mosques, searchQuery, filters]);

  // Show loading state only while mosques are being fetched.
  if (isHomeLandingPage) {
    return <HomeLandingPage mosqueCount={mosques.length} isMosqueCountLoading={isMosquesLoading} />;
  }

  if (isMosquesLoading) {
    return (
      <div className="App">
        <div className="loading-screen">
          <div className="loading-content">
            <div className="loading-icon">🕌</div>
            <h1 className="loading-title">{t('loadingScreen.title')}</h1>
            <div className="loading-spinner-wrapper">
              <div className="modern-spinner"></div>
            </div>
            <p className="loading-text">
              {t('loadingScreen.loadingMosques')}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="App app-layout">
      {/* Hamburger button for mobile */}
      <HamburgerButton
        isOpen={isSidePanelOpen}
        onClick={() => setIsSidePanelOpen(!isSidePanelOpen)}
      />

      {isInfoBannerVisible && (
        <div className="info-banner" role="status">
          <div className="info-banner-content">
            <strong>{t('infoBanner.title')}</strong>
            <p>{t('infoBanner.message')}</p>
            <div className="info-banner-actions" aria-hidden="true">
              <span>{t('infoBanner.actionAdd')}</span>
              <span>{t('infoBanner.actionSuggest')}</span>
              <span>{t('infoBanner.actionReport')}</span>
            </div>
          </div>
          <button
            type="button"
            onClick={handleDismissInfoBanner}
            aria-label={t('common.close')}
          >
            ×
          </button>
        </div>
      )}

      {/* Side panel with search, filters, and list */}
      <SidePanel
        isOpen={isSidePanelOpen}
        onClose={() => setIsSidePanelOpen(false)}
        mosques={filteredMosques}
        onMosqueClick={handleMosqueClick}
        onSearch={handleSearch}
        onFilter={handleFilter}
        userLocation={userLocation}
        isLoading={isMosquesLoading}
        isAdmin={isAdmin}
        onAdminClick={() => {
          if (isAdmin) {
            setIsAdminDashboardOpen(true);
            window.history.pushState({}, '', '/admin');
          } else {
            setIsAdminLoginOpen(true);
          }
        }}
      />

      {/* Map container - always visible */}
      <div className="map-container">
        <Suspense fallback={<div className="map-loading">
          <div className="loading-spinner"></div>
          <p>{t('loading.map')}</p>
        </div>}>
          <MapComponent
            mosques={filteredMosques}
            onMapClick={handleMapClick}
            onMosqueClick={handleMosqueClick}
            userLocation={userLocation}
            centerOnUserLocation={shouldCenterOnLocation}
            onLocateMe={handleNearMe}
            isLoadingLocation={isLoadingLocation}
            pendingLocation={pendingLocation}
            onConfirmLocation={handleConfirmLocation}
            onCancelPendingLocation={handleCancelPendingLocation}
            selectedMosque={selectedMosque}
            selectedMosqueFocusKey={selectedMosqueFocusKey}
          />
        </Suspense>
      </div>

      <AddMosqueForm
        isOpen={isAddFormOpen}
        onClose={handleCloseAddForm}
        onSubmit={handleAddMosque}
        selectedLocation={selectedLocation}
      />

      <MosqueDetails
        mosque={selectedMosque}
        isOpen={isDetailsOpen}
        onClose={handleCloseDetails}
        onSuggestEdit={handleSuggestEdit}
      />

      <SuggestEditForm
        isOpen={isSuggestEditOpen}
        onClose={handleCloseSuggestEdit}
        onSubmit={handleSubmitSuggestion}
        mosque={mosqueToEdit}
      />

      {mosquesError && (
        <ErrorMessage
          message={mosquesError}
        />
      )}

      {isAdminLoginOpen && (
        <AdminLogin
          onClose={() => {
            setIsAdminLoginOpen(false);
            if (window.location.pathname === '/admin') {
              window.history.pushState({}, '', '/');
            }
          }}
          onSuccess={() => {
            setIsAdminLoginOpen(false);
            setIsAdminDashboardOpen(true);
            window.history.pushState({}, '', '/admin');
          }}
        />
      )}

      {isAdminDashboardOpen && (
        <AdminDashboard
          onClose={() => {
            setIsAdminDashboardOpen(false);
            window.history.pushState({}, '', '/');
            refetch(); // Refresh mosque list after admin changes
          }}
        />
      )}
    </div>
  );
}

export default App;
